// Translations: the 35 legacy language files converted to JSON (scripts/convert-lang.mjs), English
// bundled as the fallback, the chosen language loaded as a lazy chunk before the first render.
// Keys and `{var}` placeholders are the legacy ones (.ai/ui-and-options.md "Languages").
import enUs from './locales/en-us.json';
import { env } from '../env';

export const LANGUAGE_CODES = ['ar', 'bg', 'bs', 'ca', 'cs', 'cy', 'da', 'de', 'el', 'en-us', 'es-es', 'et', 'fi', 'fr', 'gl', 'he', 'hu', 'it', 'ja', 'ka', 'ko', 'lv', 'nl', 'nn-no', 'pl-pl', 'pt-br', 'ro', 'ru', 'sl', 'sr', 'sv-se', 'tr', 'uk', 'vi', 'zh-cn'] as const;
export type LanguageCode = (typeof LANGUAGE_CODES)[number];

/** Order of the Language tab (window/options.js:115-153). */
export const LANGUAGE_TAB_ORDER: LanguageCode[] = ['ar', 'bg', 'bs', 'zh-cn', 'ca', 'cs', 'da', 'nl', 'en-us', 'et', 'fi', 'fr', 'gl', 'ka', 'de', 'el', 'he', 'hu', 'it', 'ja', 'ko', 'lv', 'nn-no', 'pl-pl', 'pt-br', 'ro', 'ru', 'sr', 'sl', 'es-es', 'sv-se', 'tr', 'uk', 'vi', 'cy'];

const loaders = import.meta.glob<{ default: Record<string, string> }>('./locales/*.json');

type Dict = Record<string, string>;
/** Legacy texts carry `{SS}` for the static host (What's New images): filled once per load. */
const withAssets = (d: Dict): Dict => Object.fromEntries(Object.entries(d).map(([k, v]) => [k, v.includes('{SS}') ? v.split('{SS}').join(env.ss) : v]));
let data: Dict = withAssets({ ...enUs });
let current: LanguageCode = 'en-us';

const isCode = (v: string): v is LanguageCode => (LANGUAGE_CODES as readonly string[]).includes(v);

/**
 * Stored code wins; else the browser language. `pl` -> `pl-pl`. Exact match, then `xx-xx`, then
 * the bare `xx` (new: `de-DE` -> `de`; legacy gave English); empty or unknown -> `en-us`.
 */
export function resolveLanguage(stored: string, browser: string): LanguageCode {
  const raw = (stored || browser || '').toLowerCase().trim();
  if (!raw) return 'en-us';
  if (raw === 'pl') return 'pl-pl';
  if (isCode(raw)) return raw;
  const base = raw.split(/[-_]/)[0] ?? raw;
  const doubled = `${base}-${base}`;
  if (isCode(doubled)) return doubled;
  if (isCode(base)) return base;
  // Known region variants that map to the single file the app has.
  const byBase: Record<string, LanguageCode> = { en: 'en-us', es: 'es-es', pt: 'pt-br', zh: 'zh-cn', sv: 'sv-se', nn: 'nn-no', nb: 'nn-no', no: 'nn-no' };
  return byBase[base] ?? 'en-us';
}

/** Language code -> the BCP 47 tag of <html lang> (the same as server/rules/urls.js `langTag`): `lv` holds LITHUANIAN
 *  texts, `sr` is Serbian in Cyrillic. */
const LANG_TAG: Partial<Record<LanguageCode, string>> = { 'pl-pl': 'pl', 'en-us': 'en-US', 'es-es': 'es-ES', 'sv-se': 'sv-SE', 'nn-no': 'nn-NO', 'pt-br': 'pt-BR', 'zh-cn': 'zh-CN', lv: 'lt', sr: 'sr-Cyrl' };
export const langTag = (code: LanguageCode): string => LANG_TAG[code] ?? code;

export async function loadLanguage(code: LanguageCode): Promise<void> {
  current = code;
  // The page says the language it shows (the template says Polish): a German page marked Polish made Chrome translate
  // it, and the translation broke React (report 2026-10-26, ui/domGuard.ts). Only `lang`: the board is never mirrored.
  if (typeof document !== 'undefined') document.documentElement.lang = langTag(code);
  if (code === 'en-us') {
    data = withAssets({ ...enUs });
    return;
  }
  const load = loaders[`./locales/${code}.json`];
  const mod = load ? await load() : null;
  data = withAssets({ ...enUs, ...(mod?.default ?? {}) });
}

export const currentLanguage = (): LanguageCode => current;

/** Translate; `{name}` placeholders are replaced from `vars`. Unknown keys give `''` like legacy. */
export function t(key: string, vars?: Record<string, string | number>): string {
  let s = data[key] ?? '';
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
  return s;
}

export const hasKey = (key: string): boolean => key in data;

/** The label of a language in the Options window, e.g. "Polish - Polski". */
export function languageName(code: LanguageCode): string {
  const id = (code === 'pl-pl' ? 'pl' : code).replace(/-(\w)/g, (_, c: string) => c.toUpperCase());
  return t('optionWindowTabSettingsLang' + id.charAt(0).toUpperCase() + id.slice(1));
}
